import React, { useEffect, useMemo, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";
import { useIntl } from "react-intl";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  ThemeProvider,
} from "@mui/material";
import { ErrorText } from "../styled";
import { getAlertThemeData, getMuiTheme } from "../../../../../utils/utils.js";
import palette from "../../../../../../config/colorPalette.js";
import { TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { postAlertConfiguration } from "../../../../../api/index.js";
import { setConfigDataAction } from "../../../../../redux/configSlice.js";

const AlertConfig = ({
  open,
  setOpen,
  configData,
  equipment,
  equipmentTypes,
  assetMeasurands,
  assetModules,
  assetAdditionalSensors,
  assetExternalServices,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useMemo(() => getMuiTheme(), []);
  const [listCategory, setListCategory] = useState(["measurand", "module", "equipment", "sensor", "external_service"]);
  const [data, setData] = useState(null);
  const [alertName, setAlertName] = useState("");
  const [alertCategory, setAlertCategory] = useState("measurand");
  const [alertType, setAlertType] = useState("");
  const [alertSubType, setAlertSubType] = useState(null);
  const newConfig = {
    measurands: assetMeasurands,
    modules: assetModules,
    additionalSensors: assetAdditionalSensors,
    externalServices: assetExternalServices,
  };
  useEffect(() => {
    let finalData = {};
    let newList = clone(listCategory);

    for (let indexCategory = 0; indexCategory < listCategory.length; indexCategory++) {
      if (listCategory[indexCategory] === "measurand") {
        const equipmentData = equipmentTypes.find((et) => et.name === equipment.equipmentType);
        finalData[listCategory[indexCategory]] = equipmentData.measurands;
        setAlertType(equipmentData.measurands[0]);
      }
      if (
        listCategory[indexCategory] === "module" &&
        !isNil(newConfig.config) &&
        !isNil(newConfig.config.modules) &&
        !isEmpty(newConfig.config.modules)
      ) {
        if (!isNil(newConfig.config)) {
          for (let indexModule = 0; indexModule < newConfig.config.modules.length; indexModule++) {
            const moduleData = newConfig.config.modules[indexModule];
            finalData[listCategory[indexCategory]] = {};
            const packageList = [];
            for (let indexPackage = 0; indexPackage < moduleData.packages.length; indexPackage++) {
              packageList.push(moduleData.packages[indexPackage].name);
            }
            finalData[listCategory[indexCategory]][moduleData.name] = packageList;
          }
        }
      } else if (listCategory[indexCategory] === "module") {
        newList = newList.filter(function (item) {
          return item !== "module";
        });
      }

      if (listCategory[indexCategory] === "equipment") {
        finalData[listCategory[indexCategory]] = {
          stats: ["data_disk_usage", "cpu_usage"],
          error_codes: ["fiber_break", "common"],
        };
      }
      if (listCategory[indexCategory] === "sensor") {
        if (!isNil(newConfig.config) && !isNil(newConfig.config.additionalSensors)) {
          const measurands = newConfig.config.additionalSensors.measurands;

          if (!finalData[listCategory[indexCategory]]) {
            finalData[listCategory[indexCategory]] = {};
          }

          for (let indexSensor = 0; indexSensor < measurands.length; indexSensor++) {
            const sensorData = measurands[indexSensor];
            const { category, reference } = sensorData;

            if (!finalData[listCategory[indexCategory]][category]) {
              finalData[listCategory[indexCategory]][category] = [];
            }

            finalData[listCategory[indexCategory]][category].push(reference);
          }
        } else {
          newList = newList.filter(function (item) {
            return item !== "sensor";
          });
        }
      }

      if (listCategory[indexCategory] === "external_service") {
        if (!isNil(newConfig.config) && !isNil(newConfig.config.externalServices)) {
          finalData[listCategory[indexCategory]] = [];
          for (let indexService = 0; indexService < newConfig.config.externalServices.length; indexService++) {
            const serviceData = newConfig.config.externalServices[indexService];
            finalData[listCategory[indexCategory]].push(serviceData.name);
          }
        } else {
          newList = newList.filter(function (item) {
            return item !== "external_service";
          });
        }
      }
    }
    setListCategory(newList);
    setData(finalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newConfig]);

  const [alertActive, setAlertActive] = useState(true);
  const [alertSeverity, setAlertSeverity] = useState(1);
  const [alertThresholdTypes, setAlertThresholdTypes] = useState([]);
  const [alertTresholdConditions, setAlertTresholdConditions] = useState([]);
  const [alertThresholdValues, setAlertThresholdValues] = useState([]);
  const [errors, setErrors] = useState([]);
  useEffect(() => {
    if (alertName.length < 3) {
      setErrors((prevErrors) => ({ ...prevErrors, "alert-name": "3.characters" }));
    } else {
      setErrors((prevErrors) => {
        const { "alert-name": alertName, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  }, [alertName, setErrors]);
  const handleClose = (setOpen) => {
    setOpen(false);
  };
  const disabled = useMemo(() => !isEmpty(errors), [errors]);

  return (
    <ThemeProvider theme={theme}>
      <Dialog maxWidth="lg" open={open} onClose={() => handleClose(setOpen)}>
        <DialogTitle style={{ fontSize: "x-large", color: palette.primary, fontWeight: "600" }}>
          {`${intl.formatMessage({ id: "alert.config" })}`}
        </DialogTitle>
        <DialogContent style={{ paddingBottom: "0" }}>
          <form autoComplete="off">
            <div>
              <Divider style={{ marginBottom: "1rem", color: palette.primary }} />
              <div style={{ display: "flex" }}>
                <FormControl>
                  <TextField
                    autoFocus
                    value={alertName}
                    variant="outlined"
                    autoComplete="off"
                    margin="dense"
                    id="alert-name"
                    label={intl.formatMessage({ id: "name" })}
                    type="text"
                    fullWidth
                    required
                    error={errors["alert-name"] ? true : false}
                    onChange={(e) => {
                      setAlertName(e.target.value);
                    }}
                  ></TextField>
                  {!isNil(errors["alert-name"]) && (
                    <ErrorText>{intl.formatMessage({ id: errors["alert-name"] })}</ErrorText>
                  )}
                  <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                    <Grid item>
                      <TextField
                        id="alertCategory"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertCategory}
                        onChange={(e) => {
                          setAlertCategory(e.target.value);
                          setAlertThresholdTypes([]);
                          setAlertTresholdConditions([]);
                          setAlertThresholdValues([]);
                          if (Array.isArray(data[e.target.value])) {
                            setAlertType(data[e.target.value][0]);
                            setAlertSubType(null);
                          } else {
                            const list = Object.keys(data[e.target.value]);
                            setAlertType(list[0]);
                            setAlertSubType(data[e.target.value][list[0]][0]);
                          }
                        }}
                        select
                        label={intl.formatMessage({ id: "alert.category" })}
                        required
                      >
                        {listCategory.map((cat) => (
                          <MenuItem key={`category_${cat}`} value={cat}>
                            {intl.formatMessage({ id: cat })}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item>
                      <TextField
                        id="alertType"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertType}
                        onChange={(e) => {
                          setAlertType(e.target.value);
                          if (!Array.isArray(data[alertCategory])) {
                            setAlertSubType(data[alertCategory][e.target.value][0]);
                          }
                        }}
                        select
                        label={intl.formatMessage({ id: "alert.type" })}
                        required
                      >
                        {!isNil(data) && Array.isArray(data[alertCategory])
                          ? data[alertCategory].map((type) => (
                              <MenuItem key={`ref_${type}`} value={type}>
                                {intl.formatMessage({ id: type })}
                              </MenuItem>
                            ))
                          : !isNil(data) &&
                            Object.keys(data[alertCategory]).map((type) => (
                              <MenuItem key={`ref_${type}`} value={type}>
                                {intl.formatMessage({ id: type })}
                              </MenuItem>
                            ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                    {!isNil(data) && !Array.isArray(data[alertCategory]) && (
                      <Grid item>
                        <TextField
                          id="alertType"
                          margin="dense"
                          sx={{ width: "10rem" }}
                          variant="outlined"
                          value={alertSubType}
                          onChange={(e) => {
                            setAlertSubType(e.target.value);
                          }}
                          select
                          label={intl.formatMessage({ id: "alert.subtype" })}
                          required
                        >
                          {!isNil(data) &&
                            !Array.isArray(data[alertCategory]) &&
                            data[alertCategory][alertType].map((subtype) => (
                              <MenuItem key={`sub_${subtype}`} value={subtype}>
                                {intl.formatMessage({ id: subtype })}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>
                    )}

                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id={"checkbox_active"}
                            sx={{
                              color: palette.primary,
                              "&.Mui-checked": {
                                color: palette.primary,
                              },
                              marginLeft: "15%",
                              marginTop: "0.5rem",
                            }}
                            defaultChecked={alertActive}
                            onChange={(e) => {
                              setAlertActive(e.target.checked);
                            }}
                          />
                        }
                        label={intl.formatMessage({ id: "active" })}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        id="alertSeverity"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertSeverity}
                        onChange={(e) => setAlertSeverity(e.target.value)}
                        select
                        label={intl.formatMessage({ id: "alert.severity" })}
                        required
                      >
                        <MenuItem key={`severity_info`} value={1}>
                          {(() => {
                            const alertData = getAlertThemeData(1, "small", intl);

                            return (
                              <Badge
                                sx={{
                                  backgroundColor: alertData.backgroundColor,
                                  justifyContent: "center",
                                  borderRadius: "16px",
                                  padding: "5px",
                                  marginRight: "0.5rem",
                                }}
                              >
                                {alertData.icon}
                              </Badge>
                            );
                          })()}
                          {intl.formatMessage({ id: "info" })}
                        </MenuItem>
                        <MenuItem key={`severity_warning`} value={2}>
                          {(() => {
                            const alertData = getAlertThemeData(2, "small", intl);

                            return (
                              <Badge
                                sx={{
                                  backgroundColor: alertData.backgroundColor,
                                  justifyContent: "center",
                                  borderRadius: "16px",
                                  padding: "5px",
                                  marginRight: "0.5rem",
                                }}
                              >
                                {alertData.icon}
                              </Badge>
                            );
                          })()}{" "}
                          {intl.formatMessage({ id: "warning" })}
                        </MenuItem>
                        <MenuItem key={`severity_critical`} value={3}>
                          {(() => {
                            const alertData = getAlertThemeData(3, "small", intl);

                            return (
                              <Badge
                                sx={{
                                  backgroundColor: alertData.backgroundColor,
                                  justifyContent: "center",
                                  borderRadius: "16px",
                                  padding: "5px",
                                  marginRight: "0.5rem",
                                }}
                              >
                                {alertData.icon}
                              </Badge>
                            );
                          })()}
                          {intl.formatMessage({ id: "critical" })}
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                  {alertCategory !== "module" && (
                    <>
                      {" "}
                      <h2>{intl.formatMessage({ id: "thresholds" })}</h2>
                      <Button
                        variant="contained"
                        style={{ backgroundColor: palette.primary, color: "white", width: "6rem", marginTop: "1%" }}
                        onClick={() => {
                          setAlertThresholdTypes([...alertThresholdTypes, "normal"]);
                          setAlertTresholdConditions([...alertTresholdConditions, ">"]);
                          setAlertThresholdValues([...alertThresholdValues, 0]);
                        }}
                      >
                        {intl.formatMessage({ id: "add.threshold" })}
                      </Button>
                      {!isEmpty(alertThresholdTypes) &&
                        alertThresholdTypes.map((at, atIndex) => {
                          return (
                            <Grid
                              spacing={1}
                              container
                              direction="row"
                              justifyContent="center"
                              sx={{ marginTop: "1%" }}
                              alignItems="flex-start"
                            >
                              <Grid item>
                                <TextField
                                  id={`alertThresholdTypes${atIndex}`}
                                  margin="dense"
                                  sx={{ width: "10rem" }}
                                  variant="outlined"
                                  value={at}
                                  onChange={(e) => {
                                    let newTypes = clone(alertThresholdTypes);
                                    newTypes[atIndex] = e.target.value;
                                    setAlertThresholdTypes(newTypes);
                                  }}
                                  select
                                  label={intl.formatMessage({ id: "threshold.type" })}
                                  required
                                >
                                  <MenuItem key={`threhsold_type_normal`} value={"normal"}>
                                    {intl.formatMessage({ id: "normal" })}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_type_average`} value={"average"}>
                                    {intl.formatMessage({ id: "average" })}
                                  </MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item>
                                <TextField
                                  id={`alertThresholdCondition${atIndex}`}
                                  margin="dense"
                                  sx={{ width: "10rem" }}
                                  variant="outlined"
                                  value={alertTresholdConditions[atIndex]}
                                  onChange={(e) => {
                                    let newConditions = clone(alertTresholdConditions);
                                    newConditions[atIndex] = e.target.value;
                                    setAlertTresholdConditions(newConditions);
                                  }}
                                  select
                                  label={intl.formatMessage({ id: "threshold.condition" })}
                                  required
                                >
                                  <MenuItem key={`threhsold_condition_>`} value={">"}>
                                    {`>`}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_>=`} value={">="}>
                                    {`>=`}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_==`} value={"=="}>
                                    {`==`}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_<=`} value={"<="}>
                                    {`<=`}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_<`} value={"<"}>
                                    {`<`}
                                  </MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item>
                                <TextField
                                  value={alertThresholdValues[atIndex]}
                                  variant="outlined"
                                  autoComplete="off"
                                  margin="dense"
                                  id={`alertThresholdValue_${atIndex}`}
                                  label={intl.formatMessage({ id: "value" })}
                                  type="number"
                                  fullWidth
                                  required
                                  error={errors[`alertThresholdValue_${atIndex}`] ? true : false}
                                  onChange={(e) => {
                                    const newErrors = { ...errors };
                                    if (isEmpty(e.target.value)) {
                                      newErrors[e.target.id] = "not.empty";
                                    } else {
                                      delete newErrors[e.target.id];
                                    }
                                    setErrors(newErrors);
                                    let newValues = clone(alertThresholdValues);
                                    newValues[atIndex] = e.target.value;
                                    setAlertThresholdValues(newValues);
                                  }}
                                  typ
                                ></TextField>
                              </Grid>
                            </Grid>
                          );
                        })}
                    </>
                  )}
                </FormControl>
              </div>
              <Divider style={{ marginTop: "1rem" }} />
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            style={{ backgroundColor: palette.secondary, color: "white" }}
            onClick={() => handleClose(setOpen)}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          {
            <Button
              variant="contained"
              style={{
                backgroundColor: disabled ? palette.light : palette.primary,
              }}
              id={"save_data_button"}
              disabled={disabled}
              onClick={async () => {
                const thresholds = [];
                for (let index = 0; index < alertThresholdTypes.length; index++) {
                  const newTH = {
                    type: alertThresholdTypes[index],
                    condition: alertTresholdConditions[index],
                    value: alertThresholdValues[index],
                  };
                  thresholds.push(newTH);
                }
                const finalData = {
                  name: alertName,
                  desc: null,
                  type: alertType,
                  category: alertCategory,
                  subtype: alertSubType,
                  active: alertActive,
                  severity: alertSeverity,
                  conditions: thresholds,
                };
                const response = await postAlertConfiguration(finalData, intl);
                if (response?.status === 200) {
                  dispatch(
                    setConfigDataAction({
                      ...configData,
                      alertConfiguration: [...configData.alertConfiguration, finalData],
                    }),
                  );
                  handleClose(setOpen);
                }
              }}
            >
              {intl.formatMessage({ id: "accept" })}
            </Button>
          }
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AlertConfig;
